import { inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { map, Observable } from 'rxjs'
import { environment } from '@environments/environment'
import { CertificateHolderApiResponse, PaginatedCertificateHolderApiResponse } from '@features/certificate-holder/models/certificate-holder.model'

@Injectable({
  providedIn: 'root'
})
export class CertificateHolderService {
  private readonly http = inject(HttpClient)
  private readonly apiUrl = environment.chApiUrl

  public getById(uuid: string): Observable<CertificateHolderApiResponse> {
    return this.http.get<CertificateHolderApiResponse>(`${this.apiUrl}/${uuid}`)
  }

  public listAll(): Observable<PaginatedCertificateHolderApiResponse> {
    return this.http.get<PaginatedCertificateHolderApiResponse>(`${this.apiUrl}/navigation-list`)
  }

  /** Server-side search over company name and recipient email address. */
  public search(query: string): Observable<CertificateHolderApiResponse[]> {
    return this.http.get<CertificateHolderApiResponse[]>(`${this.apiUrl}/search`, { params: { q: query } })
  }

  public create(data: CertificateHolderApiResponse): Observable<CertificateHolderApiResponse> {
    return this.http.post<CertificateHolderApiResponse>(`${this.apiUrl}`, data)
  }

  public update(data: CertificateHolderApiResponse): Observable<CertificateHolderApiResponse> {
    return this.http.put<CertificateHolderApiResponse>(`${this.apiUrl}`, data)
  }

  public checkCertificateHolderExists(companyName: string, country: string, freetext: string): Observable<boolean> {
    return this.http.post<{ exist: boolean }>(`${this.apiUrl}/exists`, {
      companyName: companyName,
      country: country,
      freetext: freetext
    }).pipe(
      map(response => response.exist)
    )
  }

  public delete(uuid: string): Observable<boolean> {
    return this.http.delete<{ status: string, message: string }>(`${this.apiUrl}/${uuid}`)
      .pipe(
        map(response => response.status === 'OK')
      )
  }

  public save(data: CertificateHolderApiResponse): Observable<CertificateHolderApiResponse> {
    const uuidValue = data.uuid

    const hasUuid = uuidValue !== null &&
      uuidValue !== undefined
    return hasUuid
      ? this.update(data)
      : this.create(data)
  }
}
